import AddIcon from "@mui/icons-material/AddCircleOutlineRounded";
import { default as DeleteIcon } from "@mui/icons-material/HighlightOffRounded";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import {
  AccordionHeader,
  AccordionHeaderText,
  FormController,
  MuiBox,
  MuiButtonTertiary,
  MuiGrid,
  MuiSelect,
  MuiTextField,
  WarningConfirmationModal
} from "../../../../../components";

import { useGetLanguages } from "../../../../../api/services/client/clientApis";
import { InformationBadge } from "../../../../../common/informationBadge/informationBadge";
import { ButtonWithIcon } from "../../../../../components/buttonWithIcon/ButtonWithIcon";
import {
  gridSize3Column,
  MAX_LENGTH_100,
  PAGE_IDS,
} from "../../../../../helpers/constants";
import {
  getFormArrayInstanceHasErrors,
  isNotEmpty,
  replaceHashWithValue,
} from "../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { useToast } from "../../../../../hooks/useToast";
import { Separator } from "../../../../../styles/common.style";
import { Resource, ResourcesFormKey } from "../../../modelsAndSchemas/Models";
import { clientSpecificContentKeys } from "../../../siteCore/SiteCoreKeys";

export const emptyResourceItem: Resource = {
  resourceName: "",
  resourceTypeId: "",
  resourceId: "",
  languageId: "",
};

interface CreateResourcesProps {
  isEdit?: boolean;
}

const pageId = PAGE_IDS.CREATE_RESOURCE;
export const CreateResources: FC<CreateResourcesProps> = ({
  isEdit = false,
}) => {
  const { control } = useFormContext();
  const { displayToast } = useToast();
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false);
  const indexRef = useRef<number>();

  const [resourceAccordionStatus, setResourceAccordionStatus] = useState<
    boolean[]
  >([]);

  const {
    commonLabels,
    fetchLabel,
    fetchDropdownValues,
    commonValidationMessages,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const { data: languagesApiData } = useGetLanguages();

  const {
    fields: resourcesArray,
    append,
    remove,
  } = useFieldArray({
    control,
    name: ResourcesFormKey.resources,
    keyName: "itemId",
  });

  const { inputPlaceHolder, selectPlaceHolder } = commonLabels;
  const {
    clientSpecificContentTitle,
    resourceName,
    resourceID,
    resourceType,
    language,
    addResourceButtonText,
    resourceHeading,
    resourceTypeList,
    addResourcesNote,
    editResourcesNote,
    deleteResourceActionText,
    resourceDeletedMessage,
  } = clientSpecificContentKeys;

  const getLabel = (key: string) => {
    return fetchLabel(clientSpecificContentTitle, key);
  };

  const languageDropdownData = useMemo(() => {
    return languagesApiData?.map((language: string) => ({
      label: language,
      value: language,
    }));
  }, [languagesApiData]);

  const handleAddResources = () => {
    append(emptyResourceItem);
  };

  const handleModalClose = () => setShowDeleteConfirmationModal(false);

  const handleDeleteResource = () => {
    if (isNotEmpty(indexRef.current)) {
      remove(indexRef.current);
      handleModalClose();
      displayToast(getLabel(resourceDeletedMessage), "success");
      indexRef.current = undefined;
    }
  };

  const onDeleteResourceClick = (index: number) => {
    indexRef.current = index;
    setShowDeleteConfirmationModal(true);
  };

  const handleResourceAccordionStatus = (index: number, expanded: boolean) => {
    const status = [...resourceAccordionStatus];
    status[index] = expanded;
    setResourceAccordionStatus(status);
  };

  useEffect(() => {
    let status = [...resourceAccordionStatus];
    resourcesArray.forEach((_field, index) => {
      status[index] = isEdit || index === resourcesArray.length - 1;
    });
    setResourceAccordionStatus(status);
  }, [resourcesArray.length]);

  const {
    formState: { errors },
  } = useFormContext();

  const resourceFormArrayHasErrors = (index: number) =>
    getFormArrayInstanceHasErrors(errors, index, ResourcesFormKey.resources, [
      ResourcesFormKey.resourceName,
      ResourcesFormKey.resourceTypeId,
      ResourcesFormKey.resourceId,
      ResourcesFormKey.languageId,
    ]);

  const renderSubHeader = (index: number) => {
    return resourcesArray.length > 1 ? (
      <>
        <>
          <MuiBox className="semi-header">
            <ButtonWithIcon
              buttonText={commonLabels.delete}
              onActionClick={() => onDeleteResourceClick(index)}
              ActionIcon={DeleteIcon}
              data-testId={`${pageId}-${ResourcesFormKey.delete}`}
            />
          </MuiBox>
          <Separator />
        </>
      </>
    ) : (
      <></>
    );
  };

  return (
    <MuiBox data-testid="resource-tab">
      <InformationBadge
        className="info-batch"
        message={getLabel(isEdit ? editResourcesNote : addResourcesNote)}
      />
      {resourcesArray.map((field, index) => {
        const label = replaceHashWithValue(
          getLabel(resourceHeading),
          `${index + 1}`,
        );
        return (
          <AccordionHeader
            key={field.itemId}
            title={
              <AccordionHeaderText
                data-testId={`${pageId}-${ResourcesFormKey.header}`}
                label={label}
              />
            }
            className="accordion-resource-header"
            onChange={(e, expanded) =>
              handleResourceAccordionStatus(index, expanded)
            }
            isExpanded={resourceAccordionStatus[index]}
            isFormArrayError={!resourceFormArrayHasErrors(index)}
          >
            {renderSubHeader(index)}
            <MuiGrid container columnSpacing={2} className="flex-row">
              <MuiGrid size={gridSize3Column}>
                <FormController
                  control={control}
                  name={`${ResourcesFormKey.resources}[${index}].${ResourcesFormKey.resourceName}`}
                >
                  <MuiTextField
                    label={getLabel(resourceName)}
                    id={`${pageId}-${ResourcesFormKey.resourceName}-${index}`}
                    isMandatory
                    placeholder={inputPlaceHolder}
                    maxLength={MAX_LENGTH_100}
                    showCloseIcon
                    requiredARIALabel={commonARIALabel.required}
                    tooltipMessage={
                      commonValidationMessages.fieldSpecialCharactersTooltipMessage
                    }
                    tooltipHeaderTitle={
                      commonValidationMessages.allowedSpecialCharactersTitle
                    }
                  />
                </FormController>
              </MuiGrid>

              <MuiGrid size={gridSize3Column}>
                <FormController
                  control={control}
                  name={`${ResourcesFormKey.resources}[${index}].${ResourcesFormKey.resourceTypeId}`}
                >
                  <MuiSelect
                    label={getLabel(resourceType)}
                    options={fetchDropdownValues(
                      clientSpecificContentTitle,
                      resourceTypeList,
                    )}
                    visibleDefaultSelectOption
                    id={`${pageId}-${ResourcesFormKey.resourceTypeId}-${index}`}
                    isMandatory
                    placeholder={selectPlaceHolder}
                    requiredARIALabel={commonARIALabel.required}
                  />
                </FormController>
              </MuiGrid>

              <MuiGrid size={gridSize3Column}>
                <FormController
                  control={control}
                  name={`${ResourcesFormKey.resources}[${index}].${ResourcesFormKey.resourceId}`}
                >
                  <MuiTextField
                    label={getLabel(resourceID)}
                    id={`${pageId}-${ResourcesFormKey.resourceId}-${index}`}
                    isMandatory
                    placeholder={inputPlaceHolder}
                    showCloseIcon
                    requiredARIALabel={commonARIALabel.required}
                  />
                </FormController>
              </MuiGrid>

              <MuiGrid size={gridSize3Column}>
                <FormController
                  control={control}
                  name={`${ResourcesFormKey.resources}[${index}].${ResourcesFormKey.languageId}`}
                >
                  <MuiSelect
                    label={getLabel(language)}
                    options={languageDropdownData ?? []}
                    visibleDefaultSelectOption
                    id={`${pageId}-${ResourcesFormKey.languageId}-${index}`}
                    isMandatory
                    showSearch
                    placeholder={selectPlaceHolder}
                    requiredARIALabel={commonARIALabel.required}
                  />
                </FormController>
              </MuiGrid>
            </MuiGrid>
          </AccordionHeader>
        );
      })}
      <MuiBox className="add-resource-button-container">
        <MuiButtonTertiary
          onClick={handleAddResources}
          id={`${pageId}-${ResourcesFormKey.addResourceButton}`}
          className="add-resource-button"
        >
          <AddIcon className="add-icon" />
          {getLabel(addResourceButtonText)}
        </MuiButtonTertiary>
      </MuiBox>
      {showDeleteConfirmationModal && (
        <WarningConfirmationModal
          show={showDeleteConfirmationModal}
          modalTitle={commonLabels.confirmation}
          onClose={handleModalClose}
          onPrimaryButtonClick={handleDeleteResource}
          labelContent={getLabel(deleteResourceActionText)}
        />
      )}
    </MuiBox>
  );
};
